import React, {useEffect, useState} from 'react';
import {Checkbox, Confirm, DataList, Modal, Spinner} from "@tag/tag-components-react-v4";
import {OrganisationDTO} from "../../../types/OrganisationDTO";
import {DivisionsWithPolicyTypes, SelectableItem} from "../../../types/PolicyTypeSelectListItem";
import PolicyTypeService from "../../../services/PolicyTypeService";
import { ChevronDownFilled, ChevronUpFilled } from '@tag/tag-icons';
import { ProjectAccents } from "../../../../General/config/projectAccents";

function PushPoliciesModal(props: {
    visible: boolean,
    isUserAllowed: boolean,
    selectedOrganisation: OrganisationDTO,
    handleClose: Function,
    setAlert: Function
}) {
    if (!props.isUserAllowed) {
        throw new Error() // todo abbas put a message
    }

    const [selectableData, setSelectableData] = React.useState<SelectableItem[]>([]);
    const [data, setData] = useState<SelectableItem[]>(selectableData);
    const [loading, setLoading] = useState<string | boolean>(false);
    const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
    const [updateConfirmed, setUpdateConfirmed] = useState<boolean>(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setUpdateConfirmed(false); // reset it
            var divisionsWithPolicyTypes: DivisionsWithPolicyTypes[] = await PolicyTypeService.getSelectablePolicyTypes(props.selectedOrganisation.id, props.selectedOrganisation.environmentName);

            // Flatten divisions and their policyTypes into a single-level array
            const selectablePolicyTypes: SelectableItem[] = divisionsWithPolicyTypes.flatMap((division: any) => [
                {
                    id: division.id,
                    name: division.name,
                    isDisabled: division.isDisabled,
                    isSelected: division.isSelected,
                    isChecked: false,
                    type: "division",
                    isCollapsed: true
                },
                ...division.policyTypes.map((policyType: any) => ({
                    ...policyType,
                    divisionId: division.id,
                    isChecked: false,
                    type: "policyType"
                }))
            ]);

            setSelectableData(selectablePolicyTypes);
            setLoading(false);
        };

        fetchData();
    }, [updateConfirmed])

    //Filter out policyTypes that belong to collapsed divisions while keeping other properties intact so that policyTypes can remain selected even if their parent division is collapsed
    useEffect(() => {
        setData(
            selectableData.filter(item => {
                if (item.type === 'division') return true;
                const parentDivision = selectableData.find(d => d.id === item.divisionId && d.type === 'division');
                return parentDivision && !parentDivision.isCollapsed;
            })
        );
    }, [selectableData])

    const renderModalContent = () => {
        let modalContent;

        if (loading) {
            return <Spinner>{loading}</Spinner>;
        } else {
            modalContent = (
                <DataList
                    data={data}
                    renderItem={(item) =>
                        <>
                        {item.type === 'division' ? 
                            <div 
                                style={{width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12}}
                                onClick={() => {
                                    setSelectableData(prevData => 
                                        prevData.map(i => 
                                            i.id === item.id ? {...i, isCollapsed: !i.isCollapsed} : i
                                        )
                                    );
                                }}
                            >
                                <div style={{display: 'flex', alignItems: 'center', wordBreak: 'break-word'}}>
                                    <Checkbox 
                                        checked={item.isSelected || item.isChecked}
                                        disabled={item.isDisabled}
                                        onChange={(e: any) => {
                                            setSelectableData(prevData =>
                                                prevData.map(i => {
                                                    if (i.id === item.id) {
                                                        // Update the division's checked state
                                                        return { ...i, isChecked: e.checked };
                                                    }
                                                    // If this is a policyType under the division, update its checked state too
                                                    if (i.type === 'policyType' && i.divisionId === item.id && !i.isDisabled && !i.isSelected) {
                                                        return { ...i, isChecked: e.checked };
                                                    }
                                                    return i;
                                                })
                                            );
                                        }}
                                        //To prevent collapsing when clicking the checkbox
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                    />
                                    <b>{item.name}</b>
                                </div>
                                {item.isCollapsed ? <ChevronUpFilled style={{minWidth: 'fit-content'}} /> : <ChevronDownFilled style={{minWidth: 'fit-content'}} />}
                                
                            </div>
                            : 
                            <div style={{width: '100%', display: 'flex', paddingLeft: 24, alignItems: 'center', wordBreak: 'break-word'}}>
                                <Checkbox
                                    checked={item.isSelected || item.isChecked}
                                    disabled={item.isDisabled}
                                    onChange={(e) => {
                                        setSelectableData(prevData => {
                                            // Update the checked state of the policyType
                                            const updatedData = prevData.map(i =>
                                                i.id === item.id ? { ...i, isChecked: e.checked } : i
                                            );

                                            // Find the divisionId for this policyType
                                            const divisionId = item.divisionId;
                                            if (!divisionId) return updatedData;

                                            // Get all policyTypes under this division
                                            const siblingPolicyTypes = updatedData.filter(
                                                i => i.type === 'policyType' && i.divisionId === divisionId
                                            );

                                            // Check if all are checked
                                            const allChecked = siblingPolicyTypes.every(i => i.isChecked || i.isSelected);

                                            return updatedData.map(i => {
                                                if (i.id === divisionId && i.type === 'division') {
                                                    return { ...i, isChecked: e.checked ? allChecked : false };
                                                }
                                                return i;
                                            });
                                        });
                                    }}
                                />
                                <span>{item.name}</span>
                            </div>                                
                        }
                        </>
                    }
                />
            )

            return modalContent;
        }
    }

    let content;
    const isSaveButtonDisabled = selectableData.every(item => !item.isChecked)

    content = (
        <Modal
            visible={props.visible}
            accent={ProjectAccents.PoliciesAndProcedures}
            heading={'Push Policy Types to ' + props.selectedOrganisation.displayedName}
            onCancelButtonClick={() => {
                props.handleClose();
            }}
            onClose={() => props.handleClose()}
            primaryButtonProps={{text: 'Save/Push', disabled: isSaveButtonDisabled}}
            onPrimaryButtonClick={(e) => {
                e.detail.cancel = true;
                setConfirmationModalVisible(true);
            }}
        >
            <div>
                {renderModalContent()}
            </div>
        </Modal>
    )

    return (
        <>
            {content}

            <Confirm
                visible={confirmationModalVisible}
                type="question"
                heading="Confirmation"
                accent={ProjectAccents.PoliciesAndProcedures}
                onPrimaryButtonClick={() => {
                    const newlySelectedItems = 
                        selectableData.filter((item: SelectableItem) => item.type === 'policyType' && item.isChecked && !item.isDisabled && !item.isSelected).map((item: SelectableItem) => item.id);
                    setLoading(true);
                    PolicyTypeService.updatePolicyTypesWithNewPolicyTypes(props.selectedOrganisation.id, props.selectedOrganisation.environmentName, newlySelectedItems)
                        .then((result) => {
                            props.setAlert({type: 'success', message: 'Policy Types pushed successfully'});
                        })
                        .catch((res) => {
                            props.setAlert({type: 'error', message: res.message || 'An error has occurred, please contact HSC Care DevOps'});
                        })
                        .finally(() => {
                            setUpdateConfirmed(true)
                            setLoading(false)
                            setConfirmationModalVisible(false)
                        });
                }}
                onCancelButtonClick={() => {
                    setConfirmationModalVisible(false)
                }}
                onClose={() => setConfirmationModalVisible(false)}
            >
                Are you sure you want to push the selected Policy Types?
            </Confirm>
        </>
    )

}

export default PushPoliciesModal;
